import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STAGE_LABELS, STAGES, POSITIONING, CALL_SCRIPT } from "../lib/constants";
import NewLeadDialog from "../components/NewLeadDialog";
import LeadTotalsDialog from "../components/LeadTotalsDialog";
import TechSalesDialog from "../components/TechSalesDialog";
import ConversionDialog from "../components/ConversionDialog";
import GareDialog from "../components/GareDialog";
import Hotpoints from "../components/Hotpoints";
import PanoramicaTempi from "../components/PanoramicaTempi";
import PreventiviAttesa from "../components/PreventiviAttesa";
import LeadChannels from "../components/LeadChannels";
import Pipeline from "./Pipeline";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, TrendingUp, Flame, Loader2, SunMedium, Building2, Zap, Phone, Send, HardHat } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";

function Kpi({ icon: Icon, label, value, sub, testid, onClick }) {
  return (
    <Card onClick={onClick} className={`p-5 rounded border-border ${onClick ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-bold">{label}</span>
        <Icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
      </div>
      <div className="mt-3 text-3xl font-heading font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function PositionCard({ icon: Icon, block }) {
  return (
    <Card className="rounded border-border p-5" data-testid={`posizionamento-${block === POSITIONING.azienda ? "azienda" : "divisione"}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center"><Icon className="h-4 w-4 text-primary" strokeWidth={1.5} /></div>
        <h3 className="font-heading font-semibold">{block.title}</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{block.text}</p>
      <div className="flex flex-wrap gap-1.5 mt-4">
        {block.keywords.map((k) => <Badge key={k} variant="outline" className="border-primary/40 text-primary font-normal">{k}</Badge>)}
      </div>
    </Card>
  );
}

function Posizionamento() {
  return (
    <div className="space-y-6" data-testid="posizionamento-section">
      <div className="grid md:grid-cols-2 gap-4">
        <PositionCard icon={Building2} block={POSITIONING.azienda} />
        <PositionCard icon={Zap} block={POSITIONING.divisione} />
      </div>

      <Card className="rounded border-border p-5" data-testid="posizionamento-scripts">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded bg-primary/10 flex items-center justify-center"><Phone className="h-4 w-4 text-primary" strokeWidth={1.5} /></div>
          <h3 className="font-heading font-semibold">Vari script — Prima telefonata</h3>
        </div>
        <div className="space-y-4">
          {CALL_SCRIPT.map((s, i) => (
            <div key={i} className="border-l-2 border-primary/40 pl-3">
              <div className="text-xs uppercase tracking-widest text-primary font-bold mb-0.5">{s.title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const eurK = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [totOpen, setTotOpen] = useState(false);
  const [tsOpen, setTsOpen] = useState(false);
  const [pipeStage, setPipeStage] = useState(null);
  const [convOpen, setConvOpen] = useState(false);
  const [gareOpen, setGareOpen] = useState(false);
  const { user } = useAuth();
  const defaultTab = "panoramica";

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data));
  useEffect(() => { load(); }, []);

  if (!stats) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const stageData = STAGES.map((s) => ({ name: STAGE_LABELS[s], key: s, value: stats.by_stage[s] || 0 }));

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Dashboard</h1>
          <p className="text-xs text-muted-foreground">Panoramica del processo commerciale</p>
        </div>
        <NewLeadDialog onCreated={load} />
      </header>

      <div className="p-4 lg:p-8">
        <Tabs defaultValue={defaultTab}>
          <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
            <TabsTrigger value="panoramica" data-testid="tab-panoramica" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Panoramica</TabsTrigger>
            <TabsTrigger value="posizionamento" data-testid="tab-posizionamento" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Posizionamento</TabsTrigger>
          </TabsList>


          <TabsContent value="panoramica" className="space-y-6 mt-0">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Users} label="Lead Totali" value={stats.total} sub={stats.categories ? `${stats.categories.commerciali} in pipeline · clicca per dettaglio` : undefined} testid="kpi-total" onClick={() => setTotOpen(true)} />
          <LeadTotalsDialog open={totOpen} onOpenChange={setTotOpen} stats={stats} />
          <Kpi icon={Flame} label="Technical Sales (Liv. 3)" value={stats.technical_sales_active ?? 0} sub="Progetti attivi (come pagina Progetti) · clicca per dettaglio" testid="kpi-high" onClick={() => setTsOpen(true)} />
          <TechSalesDialog open={tsOpen} onOpenChange={setTsOpen} items={stats.technical_sales_all || []} />
          <Dialog open={!!pipeStage} onOpenChange={(o) => { if (!o) { setPipeStage(null); load(); } }}>
            <DialogContent className="max-w-[96vw] w-[96vw] h-[90vh] overflow-y-auto" data-testid="stage-pipeline-dialog" aria-describedby={undefined}>
              <DialogTitle className="sr-only">Pipeline</DialogTitle>
              {pipeStage && <Pipeline embedded focusStage={pipeStage} />}
            </DialogContent>
          </Dialog>
          <Kpi icon={SunMedium} label="Potenza Stimata" value={`${stats.total_kwp ?? 0} kWp`} sub="Pipeline fotovoltaico" testid="kpi-kwp" />
          <Kpi icon={TrendingUp} label="Venduto / Preventivato" value={`${stats.conversione_base?.vend_prev_pct ?? 0}%`} sub={`${eurK(stats.conversione_base?.venduto)} su ${eurK(stats.conversione_base?.preventivato)} · conversione ${stats.conversione_base?.pct ?? 0}% · clicca per dettaglio`} testid="kpi-conversion" onClick={() => setConvOpen(true)} />
          <ConversionDialog open={convOpen} onOpenChange={setConvOpen} c={stats.conversione_base || {}} />
          <Kpi icon={HardHat} label="Gara installatori" value={(stats.gare_installatori || []).length} sub={`${(stats.gare_installatori || []).filter((g) => g.esito === "in_attesa").length} in attesa · ${(stats.gare_installatori || []).filter((g) => g.esito === "assegnata").length} assegnate · clicca per dettaglio`} testid="kpi-gare" onClick={() => setGareOpen(true)} />
          <GareDialog open={gareOpen} onOpenChange={setGareOpen} items={stats.gare_installatori || []} />
        </div>

        <PanoramicaTempi />

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 p-5 rounded border-border">
            <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">Distribuzione per fase</h2>
            <p className="text-[11px] text-muted-foreground mb-4" data-testid="stage-chart-note">Lead per fase attuale, esclusi Area Free, Area Rete, parcheggiati e archiviati. Clicca una barra per aprire la pipeline.</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stageData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#888" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid #222", borderRadius: 4, fontSize: 12 }} labelStyle={{ color: "#fff" }} itemStyle={{ color: "#FDD003" }} formatter={(v) => [v, "Lead"]} />
                <Bar dataKey="value" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d) => setPipeStage(d?.payload?.key || d?.key)} data-testid="stage-bars">
                  {stageData.map((e, i) => <Cell key={i} fill="hsl(var(--primary))" data-testid={`stage-bar-${e.key}`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Hotpoints />
        </div>

        <PreventiviAttesa />
        {user?.role === "admin" && <LeadChannels />}
          </TabsContent>

          <TabsContent value="posizionamento" className="mt-0">
            <Posizionamento />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
